import { useState, useMemo, useEffect } from "react";
import Autoplay from "embla-carousel-autoplay";
import { Newspaper } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import NewsCard from "./news/NewsCard";
import NewsPopup from "./news/NewsPopup";
import { NewsItem } from "@/types/news";
import { useTheme } from "@/context/ThemeContext";
import { useNews } from "@/hooks/useNews";
import { useNewsManagement } from "@/hooks/useNewsManagement";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { getNewsUITextLabel } from "@/utils/newsUtils";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { cn } from "@/lib/utils";

const NewsCarousel = () => {
  const [popupNews, setPopupNews] = useState<NewsItem | null>(null);
  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: 20000,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        playOnInit: true,
      }),
    [],
  );
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { isNative } = useNativePlatform();
  const { user } = useAuth();

  const isAdmin = user?.type === UserType.Admin;

  const publicNewsQuery = useNews(
    user && user.id && !isAdmin
      ? {
          userType: user.type ?? "private",
          userId: String(user.id),
        }
      : undefined,
  );

  const adminNewsQuery = useNewsManagement(
    user && user.id && isAdmin
      ? {
          userType: user.type,
          userId: user.id,
          page: 1,
        }
      : undefined,
  );

  const { news, isLoading } = isAdmin
    ? {
        news: adminNewsQuery.news.filter((item) => item.visible),
        isLoading: adminNewsQuery.isLoading,
      }
    : {
        news: publicNewsQuery.news,
        isLoading: publicNewsQuery.isLoading,
      };

  const handleNewsClick = (newsItem: NewsItem) => {
    setPopupNews(newsItem);
  };

  const closePopup = () => {
    setPopupNews(null);
  };

  const canScroll = !isLoading && news.length > 1;

  useEffect(() => {
    if (!carouselApi || !canScroll) return;
    if (carouselApi.scrollSnapList().length <= 1) return;

    const autoplay = carouselApi.plugins()?.autoplay as unknown as
      | { play: () => void }
      | undefined;
    try {
      autoplay?.play();
    } catch {
      // Ignore: plugin may not have initialized when snaps ≤ 1
    }
  }, [carouselApi, canScroll]);

  return (
    <div className="w-full">
      <div className="flex items-center mb-3">
        <div className="flex items-center gap-2">
          <Newspaper className={`h-3 w-3 sm:h-5 sm:w-5`} />
          <h2
            className={cn(
              "font-semibold",
              isNative ? "text-lg sm:text-xl" : "text-sm sm:text-xl",
            )}
          >
            {getNewsUITextLabel("latestNews")}
          </h2>
        </div>
      </div>

      <Carousel
        setApi={setCarouselApi}
        opts={{
          align: "start",
          loop: canScroll,
          watchDrag: canScroll,
        }}
        plugins={canScroll ? ([autoplayPlugin] as never) : undefined}
        className="w-full"
      >
        <CarouselContent className="-ml-4">
          {isLoading ? (
            <CarouselItem className="pl-4 basis-full">
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-500">
                  {getNewsUITextLabel("loadingNews")}
                </span>
              </div>
            </CarouselItem>
          ) : news.length > 0 ? (
            news.map((newsItem, index) => (
              <CarouselItem
                key={newsItem.id}
                className="pl-4 basis-full md:basis-1/2"
              >
                <NewsCard
                  news={newsItem}
                  index={index}
                  onNewsClick={handleNewsClick}
                />
              </CarouselItem>
            ))
          ) : (
            <CarouselItem className="pl-4 basis-full">
              <div className="flex items-center justify-center h-64">
                <span className="text-gray-500">
                  {getNewsUITextLabel("noNewsAvailable")}
                </span>
              </div>
            </CarouselItem>
          )}
        </CarouselContent>
        {canScroll ? (
          <>
            <CarouselPrevious
              className={`md:flex -left-4 ${
                isDark
                  ? "text-white border-white/20 bg-black/40 hover:bg-black/60"
                  : "text-gray-800 border-black/10 bg-white/80 hover:bg-white/90"
              }`}
            />
            <CarouselNext
              className={`md:flex -right-4 ${
                isDark
                  ? "text-white border-white/20 bg-black/40 hover:bg-black/60"
                  : "text-gray-800 border-black/10 bg-white/80 hover:bg-white/90"
              }`}
            />
          </>
        ) : null}
      </Carousel>

      <NewsPopup news={popupNews} onClose={closePopup} />
    </div>
  );
};

export default NewsCarousel;
